import React, { useState, useCallback } from "react";
import InfoIcon from "../InfoIcon";
import "./index.css";
import { useTranslation } from "react-i18next";
import { IconButton, Tooltip } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import TipBox from "components/TipBox";

// Impact categories configuration - All 16 categories
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  {
    name: "Photochemical ozone formation",
    unit: "kg NMVOC-eq",
    key: "smog",
  },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  {
    name: "Ecotoxicity, freshwater",
    unit: "CTUe",
    key: "ecotoxicity",
  },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  {
    name: "Water use",
    unit: "m³ world-eq deprived",
    key: "water_use",
  },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const SubItem = ({ label, value, secondLabel, secondValue }) => (
  <div className="subitem">
    <span className="subitemspan">{label ? `${value} ${label}` : value}</span>
    {secondValue && (
      <span className="subitemspan">
        {secondLabel ? `${secondValue} ${secondLabel}` : secondValue}
      </span>
    )}
  </div>
);

const TotalBox = ({ title, items, showInfoIcon, infoText, isSwitchable: itemIsSwitchable, currentImpactIndex, onPreviousImpact, onNextImpact, impactTotals, customTextColor }) => {
  const currentImpact = itemIsSwitchable ? IMPACT_CATEGORIES[currentImpactIndex] : null;
  
  return (
    <div className="total-box">
      <div className="total-title">
        <h3 style={{ color: customTextColor || "white" }}>{title}</h3>
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        {items.map((item, index) => (
          <div key={index} className="item">
            {item.subtitle || item.noSubtitle || itemIsSwitchable ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {(item.subtitle || itemIsSwitchable) && (
                  <div className="sub-item" style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center", flexWrap: "nowrap" }}>
                    {itemIsSwitchable ? (
                      <>
                        <Tooltip title="Previous impact category">
                          <IconButton
                            size="small"
                            onClick={onPreviousImpact}
                            style={{ padding: "4px", flexShrink: 0 }}
                          >
                            <KeyboardArrowLeft fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "1", minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center", flexWrap: "wrap" }}>
                            <h4 style={{ margin: 0, fontSize: "14px", wordBreak: "break-word", hyphens: "auto", maxWidth: "280px", textAlign: "center" }}>
                              {currentImpact.name} ({currentImpact.unit})
                            </h4>
                            <TipBox
                              title={currentImpact.name}
                              message={`Impact category: ${currentImpact.name}. Unit: ${currentImpact.unit}`}
                            >
                              <IconButton size="small" style={{ padding: "2px" }}>
                                <InfoIcon />
                              </IconButton>
                            </TipBox>
                          </div>
                        </div>
                        <Tooltip title="Next impact category">
                          <IconButton
                            size="small"
                            onClick={onNextImpact}
                            style={{ padding: "4px", flexShrink: 0 }}
                          >
                            <KeyboardArrowRight fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        {item.subtitle && <h4>{item.subtitle}</h4>}
                        {showInfoIcon && <InfoIcon text={infoText} />}
                      </>
                    )}
                  </div>
                )}
                {(item.subitems || itemIsSwitchable) && (
                  <div className="subitem-box">
                    {itemIsSwitchable && impactTotals ? (
                      <SubItem
                        value={
                          typeof impactTotals[currentImpact.key] === 'number' 
                            ? impactTotals[currentImpact.key].toFixed(2) 
                            : (impactTotals[currentImpact.key] || 0)
                        }
                        label={currentImpact.unit}
                      />
                    ) : itemIsSwitchable && !impactTotals ? (
                      <SubItem
                        value="0.00"
                        label={currentImpact.unit}
                      />
                    ) : (
                      item.subitems && item.subitems.map((subitem, subIndex) => (
                        <SubItem key={subIndex} {...subitem} />
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <SubItem {...item} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const RightBox = ({ totals, lastUpdated, impactTotals = null, customTextColor }) => {
  const { t } = useTranslation();
  const [currentImpactIndex, setCurrentImpactIndex] = useState(0);

  const handlePreviousImpact = useCallback(() => {
    setCurrentImpactIndex((prev) =>
      prev > 0 ? prev - 1 : IMPACT_CATEGORIES.length - 1
    );
  }, []);

  const handleNextImpact = useCallback(() => {
    setCurrentImpactIndex((prev) =>
      prev < IMPACT_CATEGORIES.length - 1 ? prev + 1 : 0
    );
  }, []);

  return (
    <div className="right-box">
      <div style={{ display: "flex", width: "100%" }}>
        {totals && totals.length > 0 ? totals.map((total, index) => (
          <TotalBox
            key={index}
            {...total}
            isSwitchable={total.isSwitchable || false}
            currentImpactIndex={currentImpactIndex}
            onPreviousImpact={handlePreviousImpact}
            onNextImpact={handleNextImpact}
            impactTotals={impactTotals}
            customTextColor={customTextColor}
          />
        )) : (
          <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
         
          </div>
        )}
      </div>
      {lastUpdated && (
        <p className="last-updated">
          {t("LAST_UPDATED")}: {lastUpdated}
        </p>
      )}
    </div>
  );
};

export default RightBox;
